import React, { useState } from 'react';
import { Save, ChevronDown, Trash2, MoreVertical, Clock, Calendar, Activity, CheckCircle2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Lead, LeadState } from '../../types/lead';
import { IcpBadge, Badge } from '../ui/Badge';
import { EvidenceChecklist } from '../ui/EvidenceChecklist';
import { useDataStore } from '../../context/DataStoreContext';
interface LeadHeaderProps {
  lead: Lead;
  onStateChange: (newState: LeadState) => void;
}
const STATE_META: Record<LeadState, {
  label: string;
  color: string;
  dotBg: string;
  bg: string;
  text: string;
  border: string;
}> = {
  Nuevo: {
    label: 'Nuevo',
    color: 'bg-white/40',
    dotBg: 'bg-white/40',
    bg: 'bg-white/[0.04]',
    text: 'text-white/80',
    border: 'border-white/10'
  },
  Contactado: {
    label: 'Contactado',
    color: 'bg-blue-400',
    dotBg: 'bg-blue-400',
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    border: 'border-blue-500/30'
  },
  Conversación: {
    label: 'Conversación',
    color: 'bg-cyan-400',
    dotBg: 'bg-cyan-400',
    bg: 'bg-cyan-500/10',
    text: 'text-cyan-400',
    border: 'border-cyan-500/30'
  },
  'Micro-acuerdo': {
    label: 'Micro-acuerdo',
    color: 'bg-growthly-amber',
    dotBg: 'bg-growthly-amber',
    bg: 'bg-growthly-amber/10',
    text: 'text-growthly-amber',
    border: 'border-growthly-amber/30'
  },
  EnviadoPropuesta: {
    label: 'Propuesta enviada',
    color: 'bg-orange-400',
    dotBg: 'bg-orange-400',
    bg: 'bg-orange-500/10',
    text: 'text-orange-400',
    border: 'border-orange-500/30'
  },
  LeadCualificado: {
    label: 'Cualificado',
    color: 'bg-growthly-green',
    dotBg: 'bg-growthly-green',
    bg: 'bg-growthly-green/10',
    text: 'text-growthly-green',
    border: 'border-growthly-green/30'
  },
  Onboarding: {
    label: 'Onboarding',
    color: 'bg-purple-400',
    dotBg: 'bg-purple-400',
    bg: 'bg-purple-500/10',
    text: 'text-purple-400',
    border: 'border-purple-500/30'
  },
  Entregado: {
    label: 'Entregado',
    color: 'bg-emerald-500',
    dotBg: 'bg-emerald-500',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30'
  },
  Rechazado: {
    label: 'Rechazado',
    color: 'bg-growthly-red',
    dotBg: 'bg-growthly-red',
    bg: 'bg-growthly-red/10',
    text: 'text-growthly-red',
    border: 'border-growthly-red/30'
  },
  'Nutrición_T+30': {
    label: 'Nutrición T+30',
    color: 'bg-white/30',
    dotBg: 'bg-white/30',
    bg: 'bg-white/[0.04]',
    text: 'text-white/70',
    border: 'border-white/10'
  }
};
const PRIORITY_RING: Record<string, string> = {
  A: 'from-growthly-green to-emerald-600 shadow-growthly-green/30',
  B: 'from-growthly-amber to-orange-500 shadow-growthly-amber/30',
  C: 'from-white/20 to-white/10 shadow-white/5'
};
export function LeadHeader({
  lead,
  onStateChange
}: LeadHeaderProps) {
  const navigate = useNavigate();
  const {
    deleteLead
  } = useDataStore();
  const [showChecklist, setShowChecklist] = useState(false);
  const [pendingState, setPendingState] = useState<LeadState | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const states: LeadState[] = ['Nuevo', 'Contactado', 'Conversación', 'Micro-acuerdo', 'EnviadoPropuesta', 'LeadCualificado', 'Onboarding', 'Entregado', 'Rechazado', 'Nutrición_T+30'];
  const handleStateSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newState = e.target.value as LeadState;
    if (newState === 'LeadCualificado') {
      setPendingState(newState);
      setShowChecklist(true);
    } else {
      onStateChange(newState);
    }
  };
  const handleConfirmCualificado = () => {
    if (pendingState) onStateChange(pendingState);
    setShowChecklist(false);
    setPendingState(null);
  };
  const handleDelete = () => {
    if (window.confirm('¿Estás seguro de eliminar este lead? Esta acción no se puede deshacer.')) {
      deleteLead(lead.id);
      navigate('/app/leads');
    }
  };
  const stateMeta = STATE_META[lead.STATE];
  const ringGradient = PRIORITY_RING[lead.ICP_PRIORITY] || PRIORITY_RING.C;
  const createdAgo = (() => {
    const days = Math.floor((Date.now() - new Date(lead.CREATED_AT).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'hoy';
    if (days === 1) return 'ayer';
    if (days < 7) return `hace ${days} días`;
    if (days < 30) return `hace ${Math.floor(days / 7)} sem`;
    return `hace ${Math.floor(days / 30)} m`;
  })();
  const updatedAgo = (() => {
    const mins = Math.floor((Date.now() - new Date(lead.UPDATED_AT).getTime()) / (1000 * 60));
    if (mins < 1) return 'ahora mismo';
    if (mins < 60) return `hace ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `hace ${hours}h`;
    return `hace ${Math.floor(hours / 24)}d`;
  })();
  return <>
      {/* Hero header — gradient surface */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.06] via-white/[0.02] to-transparent mb-6" data-id="element-434">
        {/* Decorative orbs */}
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-growthly-amber/[0.06] rounded-full blur-3xl pointer-events-none" data-id="element-435" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-growthly-green/[0.04] rounded-full blur-3xl pointer-events-none" data-id="element-436" />

        <div className="relative p-6 md:p-7" data-id="element-437">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5" data-id="element-438">
            {/* Left: score + name */}
            <div className="flex items-start gap-5" data-id="element-439">
              {/* Score ring */}
              <div className="relative flex-shrink-0" data-id="element-440">
                <div className={`w-16 h-16 rounded-full p-[2px] bg-gradient-to-br ${ringGradient} shadow-lg`} data-id="element-441">
                  <div className="w-full h-full rounded-full bg-growthly-bg flex flex-col items-center justify-center" data-id="element-442">
                    <span className="text-[18px] font-bold text-white leading-none" data-id="element-443">
                      {lead.LEAD_SCORE}
                    </span>
                    <span className="text-[9px] text-growthly-muted uppercase tracking-wider mt-0.5" data-id="element-444">
                      score
                    </span>
                  </div>
                </div>
                <div className="absolute -bottom-1 -right-1" data-id="element-445">
                  <IcpBadge priority={lead.ICP_PRIORITY} data-id="element-446" />
                </div>
              </div>

              {/* Name + badges */}
              <div className="min-w-0" data-id="element-447">
                <div className="flex items-center gap-2 mb-1.5" data-id="element-448">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${stateMeta.bg} ${stateMeta.text} border ${stateMeta.border}`} data-id="element-449">
                    <span className={`w-1.5 h-1.5 rounded-full ${stateMeta.color} animate-pulse`} data-id="element-450" />
                    {stateMeta.label}
                  </span>
                  {lead.VERIFICADO ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-growthly-green/10 text-growthly-green border border-growthly-green/30" data-id="element-451">
                      <CheckCircle2 className="w-3 h-3" data-id="element-452" /> Verificado
                    </span> : <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-white/5 text-growthly-muted border border-white/10" data-id="element-453">
                      <AlertCircle className="w-3 h-3" data-id="element-454" /> Sin verificar
                    </span>}
                </div>
                <h1 className="text-[26px] md:text-[30px] font-bold text-white leading-tight tracking-tight truncate" data-id="element-455">
                  {lead.NOMBRE_NEGOCIO || 'Sin nombre'}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-2.5" data-id="element-456">
                  <Badge variant="outline" data-id="element-457">{lead.LEAD_SOURCE}</Badge>
                  {lead.TAG_GEO && <Badge variant="outline" data-id="element-458">📍 {lead.TAG_GEO}</Badge>}
                  {lead.DECISOR_NOMBRE && <Badge variant="outline" data-id="element-459">
                      {lead.DECISOR_NOMBRE}
                      {lead.DECISOR_CARGO && ` · ${lead.DECISOR_CARGO}`}
                    </Badge>}
                </div>
              </div>
            </div>

            {/* Right: actions */}
            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-460">
              <div className="relative" data-id="element-461">
                <select value={lead.STATE} onChange={handleStateSelect} className="appearance-none bg-growthly-surface border border-white/10 text-white text-[13px] font-medium rounded-lg pl-4 pr-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-growthly-amber hover:border-white/20 transition-colors cursor-pointer" data-id="element-462">
                  {states.map(s => <option key={s} value={s} data-id="element-463">
                      {STATE_META[s].label}
                    </option>)}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-growthly-muted pointer-events-none" data-id="element-464" />
              </div>

              <button className="inline-flex items-center bg-white text-[#0B0B0B] px-4 py-2.5 rounded-lg text-[13px] font-medium hover:bg-white/90 transition-colors" data-id="element-465">
                <Save className="w-4 h-4 mr-2" data-id="element-466" />
                Guardar
              </button>

              <div className="relative" data-id="element-467">
                <button onClick={() => setShowMenu(!showMenu)} className="p-2.5 border border-white/10 rounded-lg bg-growthly-surface text-growthly-muted hover:text-white hover:border-white/20 transition-colors" aria-label="Más opciones" data-id="element-468">
                  <MoreVertical className="w-4 h-4" data-id="element-469" />
                </button>
                {showMenu && <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} data-id="element-470" />
                    <div className="absolute right-0 mt-2 w-48 bg-growthly-surface border border-white/10 rounded-lg shadow-xl z-20 py-1 overflow-hidden" data-id="element-471">
                      <button onClick={() => {
                    setShowMenu(false);
                    handleDelete();
                  }} className="w-full text-left px-4 py-2.5 text-[13px] text-growthly-red hover:bg-growthly-red/10 flex items-center transition-colors" data-id="element-472">
                        <Trash2 className="w-4 h-4 mr-2" data-id="element-473" />
                        Eliminar lead
                      </button>
                    </div>
                  </>}
              </div>
            </div>
          </div>

          {/* Meta strip */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 pt-4 border-t border-white/5 text-[12px] text-growthly-muted" data-id="element-474">
            <span className="flex items-center gap-1.5" data-id="element-475">
              <Calendar className="w-3.5 h-3.5" data-id="element-476" />
              Creado <span className="text-white/80" data-id="element-477">{createdAgo}</span>
            </span>
            <span className="w-px h-3 bg-white/10" data-id="element-478" />
            <span className="flex items-center gap-1.5" data-id="element-479">
              <Clock className="w-3.5 h-3.5" data-id="element-480" />
              Última actividad{' '}
              <span className="text-white/80" data-id="element-481">{updatedAgo}</span>
            </span>
            <span className="w-px h-3 bg-white/10" data-id="element-482" />
            <span className="flex items-center gap-1.5" data-id="element-483">
              <Activity className="w-3.5 h-3.5" data-id="element-484" />
              Evidencia{' '}
              <span className="text-white/80" data-id="element-485">
                {lead.EVIDENCE_COMPLETENESS}%
              </span>
            </span>
            <span className="w-px h-3 bg-white/10" data-id="element-486" />
            <span className="flex items-center gap-1.5" data-id="element-487">
              ID <span className="text-white/80 font-mono" data-id="element-488">{lead.id}</span>
            </span>
          </div>
        </div>
      </div>

      {showChecklist && <EvidenceChecklist lead={lead} onClose={() => {
      setShowChecklist(false);
      setPendingState(null);
    }} onConfirm={handleConfirmCualificado} data-id="element-489" />}
    </>;
}